import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { AlertCircle, ChevronLeft } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { buildUpdateUserSchema } from '../types'
import { useUpdateUser, useUpdateUserRoles, useUser } from '../hooks/use-users'
import type { UpdateUserFormData } from '../types'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useAuth } from '@/features/auth/hooks/use-auth'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { ACTIONS } from '@/shared/constants/actions'
import { ROLES } from '@/shared/constants/roles'

export function UserDetailPage() {
  const { id } = useParams({ strict: false })
  const navigate = useNavigate()
  const parsedId = Number(id)
  const userId = Number.isFinite(parsedId) ? parsedId : null
  const { user: currentUser, hasRole } = useAuth()
  const { can } = usePermissions()
  const { t: tUsers } = useTranslation('users')
  const { t: tCommon } = useTranslation('common')
  const [tenantIdInput, setTenantIdInput] = useState(
    currentUser?.tenantId?.toString() ?? '',
  )

  const tenantId = useMemo(() => {
    if (currentUser?.tenantId) return currentUser.tenantId
    const parsed = Number(tenantIdInput)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null
  }, [currentUser?.tenantId, tenantIdInput])

  const { data, isLoading, error } = useUser(userId, tenantId)
  const updateUser = useUpdateUser(tenantId)
  const updateRoles = useUpdateUserRoles(tenantId)

  const canAssignSuperAdmin = hasRole(ROLES.SUPER_ADMIN)
  const availableRoles = Object.values(ROLES).filter(
    (role) => canAssignSuperAdmin || role !== ROLES.SUPER_ADMIN,
  )

  const [selectedRoles, setSelectedRoles] = useState<Array<string>>([])
  const updateUserSchema = useMemo(
    () => buildUpdateUserSchema(tUsers),
    [tUsers],
  )

  useEffect(() => {
    if (data?.roles) {
      setSelectedRoles(data.roles)
    }
  }, [data?.roles])

  const form = useForm<UpdateUserFormData>({
    resolver: zodResolver(updateUserSchema),
    defaultValues: {
      email: '',
      disabled: false,
    },
  })

  useEffect(() => {
    if (!data) return
    form.reset({
      email: data.email,
      disabled: Boolean(data.disabledAt),
    })
  }, [data, form])

  function toggleRole(role: string, checked: boolean) {
    setSelectedRoles((prev) => {
      const next = new Set(prev)
      if (checked) next.add(role)
      else next.delete(role)
      return Array.from(next)
    })
  }

  const onSubmit = (values: UpdateUserFormData) => {
    if (!tenantId) return
    if (!userId) return
    updateUser.mutate({ id: userId, data: values })
  }

  const handleRoleSave = () => {
    if (!tenantId) return
    if (!userId) return
    updateRoles.mutate({ id: userId, data: { roles: selectedRoles } })
  }

  if (!can(ACTIONS.USER_MANAGE)) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>{tUsers('permissions.deniedTitle')}</AlertTitle>
        <AlertDescription>
          {tUsers('permissions.deniedDescription')}
        </AlertDescription>
      </Alert>
    )
  }

  const editingSuperAdmin =
    (data?.roles.includes(ROLES.SUPER_ADMIN) ?? false) && !canAssignSuperAdmin

  return (
    <div className="space-y-6">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate({ to: '/users' })}
      >
        <ChevronLeft className="w-4 h-4 mr-2" />
        {tUsers('detail.backToList')}
      </Button>

      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/users">{tUsers('detail.breadcrumb')}</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>
              {data?.email || tUsers('detail.defaultName')}
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {canAssignSuperAdmin && !currentUser?.tenantId && (
        <div className="max-w-xs">
          <Label htmlFor="tenant-id">{tUsers('filters.tenantIdLabel')}</Label>
          <Input
            id="tenant-id"
            type="number"
            value={tenantIdInput}
            onChange={(event) => setTenantIdInput(event.target.value)}
            placeholder={tUsers('filters.tenantIdPlaceholder')}
            className="mt-1.5"
          />
        </div>
      )}

      {!tenantId && canAssignSuperAdmin && !currentUser?.tenantId && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{tUsers('detail.tenantRequiredTitle')}</AlertTitle>
          <AlertDescription>
            {tUsers('detail.tenantRequiredDescription')}
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{tCommon('error')}</AlertTitle>
          <AlertDescription>
            {tUsers('detail.errorDescription')}
          </AlertDescription>
        </Alert>
      )}

      {isLoading || !data ? (
        <div className="text-sm text-muted-foreground">
          {tUsers('detail.loading')}
        </div>
      ) : (
        <Card>
          <CardContent className="pt-6 space-y-8">
            <div>
              <h1 className="text-3xl font-bold">{data.email}</h1>
              <p className="text-muted-foreground mt-1">
                {tUsers('detail.userIdLabel')}: {data.id}
              </p>
            </div>

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4"
              >
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{tUsers('detail.emailLabel')}</FormLabel>
                      <FormControl>
                        <Input type="email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="disabled"
                  render={({ field }) => (
                    <FormItem className="flex items-center gap-3">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div>
                        <FormLabel>{tUsers('detail.disabledLabel')}</FormLabel>
                      </div>
                    </FormItem>
                  )}
                />

                <Button type="submit" disabled={updateUser.isPending}>
                  {updateUser.isPending
                    ? tCommon('saving')
                    : tUsers('detail.saveProfile')}
                </Button>
              </form>
            </Form>

            <div className="space-y-3">
              <Label>{tUsers('detail.rolesLabel')}</Label>
              {editingSuperAdmin ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>
                    {tUsers('detail.rolesNotEditableTitle')}
                  </AlertTitle>
                  <AlertDescription>
                    {tUsers('detail.rolesNotEditableDescription')}
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  <div className="space-y-2">
                    {availableRoles.map((role) => (
                      <div key={role} className="flex items-center gap-2">
                        <Checkbox
                          checked={selectedRoles.includes(role)}
                          onCheckedChange={(checked) =>
                            toggleRole(role, Boolean(checked))
                          }
                        />
                        <span className="text-sm">
                          {role.replace('_', ' ')}
                        </span>
                      </div>
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    onClick={handleRoleSave}
                    disabled={
                      updateRoles.isPending || selectedRoles.length === 0
                    }
                  >
                    {updateRoles.isPending
                      ? tCommon('saving')
                      : tUsers('detail.saveRoles')}
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
